
import { ContractInsightsData } from '@/pages/ContractDetail';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format, differenceInDays } from 'date-fns';
import { FileText, CreditCard, ExternalLink, CheckCircle, Car } from 'lucide-react';
import { openSignedUrl } from '@/lib/openSignedUrl';
import AgreementDocumentsSection from '@/components/agreements/AgreementDocumentsSection';


interface ContractInsightDocumentsProps {
    data: ContractInsightsData;
}

const ID_TYPE_LABELS: Record<string, string> = {
    DRIVING_LICENCE: 'Driving Licence',
    NATIONAL_ID: 'National ID',
};

export default function ContractInsightDocuments({ data }: ContractInsightDocumentsProps) {

    const driverIdentification = data.driver_identification || [];
    const hasIdentification = driverIdentification.length > 0;

    // THE AGREEMENT ROWS ARE NO LONGER DRAWN HERE. They are one half of a record whose other
    // half is `get_agreement_documents`, and drawing them separately printed anything signed in
    // the app twice — so both halves go to the section that can deduplicate them by arc.
    const contractAgreements = data.agreements || [];
    const depositAgreements = data.deposit_agreements || [];

    // Vehicle registrations
    const vehicleRegistrations = data.vehicle_registrations || [];
    const hasRegistrations = vehicleRegistrations.length > 0;

    // Registration type labels (database uses lowercase)
    const REGISTRATION_TYPE_LABELS: Record<string, string> = {
        register: 'New Registration',
        transfer: 'Transfer',
        registered: 'Already Registered',
    };

    // Helper to get expiry status for ID documents
    const getExpiryStatus = (expiresAt: string | null) => {
        if (!expiresAt) return { label: 'No Expiry', variant: 'secondary' as const };
        const daysUntil = differenceInDays(new Date(expiresAt), new Date());
        if (daysUntil < 0) return { label: 'Expired', variant: 'destructive' as const };
        if (daysUntil <= 30) return { label: `Expires in ${daysUntil} days`, variant: 'warning' as const };
        return { label: 'Valid', variant: 'success' as const };
    };

    // STILL COUNTS THE AGREEMENT ROWS, THOUGH IT NO LONGER DRAWS THEM. The question this
    // answers is "does this contract have anything on file at all", and an agreement is
    // something on file wherever on the page it ends up being shown.
    const hasLegacyDocuments =
        hasIdentification ||
        contractAgreements.length > 0 ||
        hasRegistrations ||
        depositAgreements.length > 0;

    return (
        <div className="space-y-8">
            {/* Every agreement on this contract, in one place: the in-app documents and the
                paper scans that have no in-app twin, deduplicated by arc. Renders nothing at all
                where the contract has neither. */}
            <AgreementDocumentsSection
                contractId={data.contract?.id}
                contractAgreements={contractAgreements}
                depositAgreements={depositAgreements}
            />

            {/* The uploaded record: identity documents, scanned agreements, registrations. Its
                empty state is about THOSE, which is why it is a sibling of the section above
                rather than a return that would hide it. */}
            {!hasLegacyDocuments && (
                <div className="border rounded-xl p-12 text-center bg-muted/20">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground/50" />
                    <p className="text-lg font-medium mt-4">No uploaded documents</p>
                    <p className="text-sm text-muted-foreground mt-1">
                        No identity documents, scanned agreements, or registrations are on file for
                        this contract.
                    </p>
                </div>
            )}

            {/* Identity Documents Section */}
            {hasIdentification && (
                <div>
                    <h3 className="text-lg font-semibold mb-4">
                        Identity Documents
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {driverIdentification.map((doc) => {
                            const status = getExpiryStatus(doc.expires_at);
                            return (
                                <Card key={doc.id} className="overflow-hidden">
                                    <CardHeader className="pb-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg bg-muted text-foreground`}>
                                                    <CreditCard className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-sm">
                                                        {ID_TYPE_LABELS[doc.type] || doc.type}
                                                    </CardTitle>
                                                    {doc.expires_at && (
                                                        <CardDescription className="text-xs">
                                                            Expires {format(new Date(doc.expires_at), 'MMM d, yyyy')}
                                                        </CardDescription>
                                                    )}
                                                </div>
                                            </div>
                                            <Badge
                                                variant={status.variant === 'success' ? 'default' : status.variant === 'warning' ? 'outline' : 'destructive'}
                                                className={status.variant === 'success' ? 'bg-green-500' : status.variant === 'warning' ? 'border-amber-500 text-amber-600' : ''}
                                            >
                                                {status.label}
                                            </Badge>
                                        </div>
                                    </CardHeader>
                                    {doc.document_url && (
                                        <CardContent className="pt-0">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="w-full"
                                                onClick={() => openSignedUrl(doc.document_url)}
                                            >
                                                View Document
                                                <ExternalLink className="h-3 w-3 ml-2" />
                                            </Button>
                                        </CardContent>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Vehicle Registrations Section */}
            {hasRegistrations && (
                <div>
                    <h3 className="text-lg font-semibold mb-4">
                        Vehicle Registrations
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {vehicleRegistrations.map((reg) => (
                            <Card key={reg.id} className="overflow-hidden">
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-muted text-foreground">
                                                <Car className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <CardTitle className="text-sm">
                                                    {REGISTRATION_TYPE_LABELS[reg.type] || reg.type}
                                                </CardTitle>
                                                <CardDescription className="text-xs">
                                                    {reg.number}
                                                </CardDescription>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge className="bg-green-500">
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                Registered
                                            </Badge>
                                        </div>
                                    </div>
                                </CardHeader>
                                {reg.certificate_url && (
                                    <CardContent className="pt-0">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full"
                                            onClick={() => openSignedUrl(reg.certificate_url)}
                                        >
                                            View Certificate
                                            <ExternalLink className="h-3 w-3 ml-2" />
                                        </Button>
                                    </CardContent>
                                )}
                            </Card>
                        ))}
                    </div>
                </div>
            )}


        </div>
    );
}
